import { useEffect, useSyncExternalStore } from "react";

import { Icon } from "@/components/layout/Icon";
import { noticePopups } from "@/lib/notify";
import { cn } from "@/lib/utils/cn";
import { Button } from "./Button";
import { Modal } from "./Modal";

/**
 * Shows what notify.error/warning raise, one popup at a time and in order.
 * Mounted once beside the toaster, above the routes, so a popup survives the
 * navigation that often follows a failure.
 */
export function NoticePopupHost() {
  const popups = useSyncExternalStore(noticePopups.subscribe, noticePopups.getSnapshot);
  const current = popups[0];
  const currentId = current?.id;

  // Modal listens for Escape on document, so a form modal open behind this
  // popup would close too and lose what was typed. Catching the key on the way
  // down keeps it from reaching any listener below.
  useEffect(() => {
    if (currentId === undefined) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      event.stopPropagation();
      noticePopups.dismiss(currentId);
    };
    document.addEventListener("keydown", onKey, true);
    return () => document.removeEventListener("keydown", onKey, true);
  }, [currentId]);

  if (!current) return null;

  const dismiss = () => noticePopups.dismiss(current.id);
  return (
    <Modal
      open
      onClose={dismiss}
      title={current.title}
      size="sm"
      footer={
        <Button autoFocus onClick={dismiss}>
          OK
        </Button>
      }
    >
      <div className="flex items-start gap-3">
        <div
          className={cn(
            "flex h-9 w-9 shrink-0 items-center justify-center rounded-full",
            current.tone === "error" ? "bg-red-50 text-red-600" : "bg-amber-50 text-amber-600",
          )}
        >
          <Icon name="alert-triangle" className="h-5 w-5" />
        </div>
        <p role="alert" className="pt-1.5 text-sm text-slate-700">
          {current.message}
        </p>
      </div>
    </Modal>
  );
}
